import React, { useEffect, useContext } from 'react'
import LoadingPanel from './loadingPanel';
import FutureCourses from './futureCourses';
import PriorCourses from './priorCourses';
import CurrentCourses from './currentCourses';
import CourseNotes from './courseNotes';
import {getEligibleCategory} from "../helpers/coursesHelper";

//import api
import { useGetCoursesSectionQuery } from '../api/courses';

//import context
import { Context } from '../courseTemplates';

/**
 * Container for all the course instance: future, prior, and current
 * @param {Object} allCourseDetails - All course details passed in through the react app entry point
 * @param {string} activeCourseName - Name of the currently active course, used for indexing
 * @param {number} courseRecordId - Course template method record id
 */
function CourseInstancesContainer({allCourseDetails, activeCourseName, courseRecordId, onlineCourseRecordId}) {
    // get our global context variables
    const value = useContext(Context);
    const { userHighestCategory } = value;

    // all the course details data for the active course
    // the active course's details that should be displayed here
    const courseDetails = allCourseDetails[activeCourseName];


    // get the future courses with the new RTK query
    // Using a query hook automatically fetches data and returns query values
    // set up refetching so we can bust the cache when accordions are opened and new data is written to WP for open seats and wailists
    const { currentData: futureCoursesCurrentData, refetch: futureCoursesRefetch} = useGetCoursesSectionQuery({templateId: courseDetails.methodRecordId, sections: 'future'});

    // get the current courses with the new RTK query
    const { currentData: currentCoursesCurrentData } = useGetCoursesSectionQuery({templateId: courseDetails.methodRecordId, sections: 'current'});

    // get the prior courses with the new RTK query
    const { currentData: priorCoursesCurrentData } = useGetCoursesSectionQuery({templateId: courseDetails.methodRecordId, sections: 'past'});

    
    // if we have our future courses data check what the highest eligible category is
    if(futureCoursesCurrentData){
        var eligibleCat = getEligibleCategory(futureCoursesCurrentData.futureCourses, userHighestCategory);
    }
    // if we have future courses data and have set the highest eligible category use it.
    const highestEligibleCategory = eligibleCat ? eligibleCat : userHighestCategory;

    // show loading if all the course sections aren't loaded in yet
    const maybeShowLoading = () => {
        return (
            !currentCoursesCurrentData ||
            !futureCoursesCurrentData ||
            !priorCoursesCurrentData
        )
    }

    //trigger on first mount and when current course instances is updated
    useEffect(() => {
        // jQuery event handlers
        initCourseEventHandlers()
        // trigger pop up init event, this is needed for some cases of the register
        // button replacement, like cancel-waitlist
        const InitPopUpsEvent = new Event('initPopups');
        document.body.dispatchEvent(InitPopUpsEvent);
        // trigger the jquery to initiate the seating popups 
        const InitSeatingPopupsEv = new Event('initSeatingPopups');
        document.body.dispatchEvent(InitSeatingPopupsEv);
    }, [currentCoursesCurrentData]);

    // Component level JSX return statement ******************************************************
    return (
        <>
            <CourseNotes courseRecordId={courseRecordId}/>
            <section id="dates" className="pattern-block">
                <div className="container">
                    {/* Show the current courses once they are loaded */}
                    {currentCoursesCurrentData?.current?.current_course_data?.length > 0 &&
                        <CurrentCourses 
                            currentCourses={currentCoursesCurrentData.current.current_course_data} 
                            courseDataJWT={currentCoursesCurrentData.current.courseDataJWT}
                            canAttendPortionOfClass={currentCoursesCurrentData.current.canAttendPortionOfClass}
                        />
                    }

                    {/* Show the future courses once they are loaded and current courses are also loaded */}
                    { currentCoursesCurrentData &&
                     futureCoursesCurrentData &&
                        <FutureCourses 
                            courseInstances={futureCoursesCurrentData.futureCourses}
                            courseRecordId={courseRecordId}
                            highestEligibleCategory={highestEligibleCategory}
                            futureCoursesRefetch={futureCoursesRefetch}
                            onlineCourseRecordId={onlineCourseRecordId}
                            onlineOnly={courseDetails.onlineOnly}/>
                    }

                    {/* Show the past courses once they are loaded and current and future courses are also loaded */}
                    {currentCoursesCurrentData &&
                     futureCoursesCurrentData &&
                     priorCoursesCurrentData?.pastCourses &&
                        <PriorCourses priorCourses={priorCoursesCurrentData.pastCourses} />
                    }

                    {/* Once everythings loaded, get rid of the loader */}
                    {maybeShowLoading() &&
                        <LoadingPanel loadText="Loading Course Instances"/>
                    }
                </div>
            </section>
        </>
    );
}

export default CourseInstancesContainer;